import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import {
  Video,
  Calendar,
  CheckCircle2,
  Search,
} from "lucide-react";
import apiClient from "../../utils/apiClient";

/**
 * MapRecordingModal
 *
 * Modal to select a batch recording and map it to a topic.
 * Fetches recordings from GET /api/batches/:batchId/recordings
 *
 * Props:
 *   - show      : Boolean
 *   - batchId   : String
 *   - topicName : String (display only)
 *   - onMap     : Function(recordingId) — async, may throw
 *   - onClose   : Function
 */
const MapRecordingModal = ({ show, batchId, topicName, onMap, onClose }) => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mapping, setMapping] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // Fetch recordings when modal opens
  useEffect(() => {
    if (!show || !batchId) return;
    const fetchRecordings = async () => {
      setLoading(true);
      setError("");
      setSelected(null);
      setSearch("");
      try {
        const { data } = await apiClient.get(
          `/batches/${batchId}/recordings?limit=100`
        );
        setRecordings(data.recordings || []);
      } catch {
        setError("Failed to load recordings.");
        setRecordings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecordings();
  }, [show, batchId]);

  const handleMap = async () => {
    if (!selected) return;
    setMapping(true);
    setError("");
    try {
      await onMap(selected);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to map recording."
      );
    } finally {
      setMapping(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  // Filter recordings by search
  const filtered = recordings.filter((r) =>
    (r.topic || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="map-recording-modal"
    >
      <Modal.Header closeButton className="mrm-header">
        <Modal.Title className="mrm-title">
          <Video size={20} className="me-2" />
          Map Recording
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="mrm-body">
        {/* Topic info */}
        <div className="mrm-topic-info">
          Mapping recording to: <strong>{topicName}</strong>
        </div>

        {/* Search */}
        <div className="mrm-search">
          <Search size={16} className="mrm-search-icon" />
          <input
            type="text"
            className="mrm-search-input"
            placeholder="Search recordings…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="mrm-loading">
            <Spinner animation="border" size="sm" />
            <span>Loading recordings…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="mrm-empty">
            <Video size={32} />
            <p>
              {recordings.length === 0
                ? "No recordings available for this batch."
                : "No recordings match your search."}
            </p>
          </div>
        ) : (
          <div className="mrm-list">
            {filtered.map((rec) => (
              <div
                key={rec._id}
                className={`mrm-item ${selected === rec._id ? "mrm-item-selected" : ""}`}
                onClick={() => setSelected(rec._id)}
                role="button"
                tabIndex={0}
              >
                {/* Thumbnail */}
                <div className="mrm-thumb">
                  {rec.thumbnail ? (
                    <img src={rec.thumbnail} alt={rec.topic || "Recording"} />
                  ) : (
                    <div className="mrm-thumb-placeholder">
                      <Video size={20} />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="mrm-item-info">
                  <span className="mrm-item-topic">
                    {rec.topic || "Untitled Recording"}
                  </span>
                  <span className="mrm-item-date">
                    <Calendar size={12} />
                    {formatDate(rec.date)}
                    {rec.fileType && (
                      <span className="mrm-file-type">{rec.fileType}</span>
                    )}
                  </span>
                </div>

                {/* Selected check */}
                {selected === rec._id && (
                  <CheckCircle2 size={20} className="mrm-item-check" />
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="mrm-footer">
        <Button variant="outline-secondary" onClick={onClose} disabled={mapping}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleMap}
          disabled={!selected || mapping}
        >
          {mapping ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Mapping…
            </>
          ) : (
            <>
              <CheckCircle2 size={16} className="me-2" />
              Map Recording
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MapRecordingModal;
